import React from 'react'
import moment from 'moment'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import SchoolIcon from '@mui/icons-material/School'
import {
  STATUS,
  getCourseStatus,
  isLegacyELearning,
} from '../../../helpers/mandatoryTrainingStatus'

// Earlier results from the retired e-learning are the only evidence held
// for this course. They no longer count.
export const hasOnlyLegacyEvidence = (course, records = [], latest) =>
  !latest &&
  records.some(
    (record) =>
      String(record.course?._id ?? record.course) === String(course._id) &&
      isLegacyELearning(record),
  )

// `eLearning` is this course's entry from /training/my-courses, when the
// course can be taken as e-learning. Its status wins over the one worked
// out from the records here: the server also applies pathway levels and
// cover from other courses.
const CourseCard = ({
  course,
  mandatoryTraining,
  eLearning,
  setSelectedCourse,
}) => {
  const { latest: latestCourse, ...recordStatus } = getCourseStatus(
    course,
    mandatoryTraining,
  )
  const pending = !!latestCourse?.requiresApproval
  let status = recordStatus
  if (eLearning && STATUS[eLearning.status]) {
    const serverStatus = STATUS[eLearning.status]
    const lapsed = ['overdue', 'notCompleted'].includes(serverStatus.key)
    status = pending && lapsed ? STATUS.pending : serverStatus
  }
  const legacyOnly = hasOnlyLegacyEvidence(
    course,
    mandatoryTraining,
    latestCourse,
  )
  const enrolment = eLearning?.enrolment
  const dateStatus = status.color === 'default' ? 'error' : status.color

  const open = () =>
    setSelectedCourse({ latestCourse, course, eLearning, legacyOnly })

  return (
    <Grid item xs={12} md={6} lg={4} xl={3}>
      <Paper
        sx={{
          border: 'solid 0.2rem',
          borderColor: `${dateStatus}.main`,
          cursor: 'pointer',
        }}
        role="button"
        tabIndex={0}
        onClick={open}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            open()
          }
        }}
      >
        <Stack padding={1} gap={1}>
          <Typography variant="title2" fontSize="1rem">
            {course.title}
          </Typography>
          {eLearning?.pathwayTitle && (
            <Typography variant="body2" color="text.secondary">
              {eLearning.pathwayTitle}
            </Typography>
          )}
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Chip
              sx={{ borderRadius: 1 }}
              size="small"
              color={dateStatus}
              label={status.key === 'pending' ? 'Pending Approval' : status.label}
            />
            {latestCourse && (
              <Chip
                sx={{ borderRadius: 1 }}
                size="small"
                variant="outlined"
                label={`Completed ${moment(latestCourse.date).format(
                  'Do MMM YYYY',
                )}`}
              />
            )}
            {latestCourse?.eLearning?.percentage != null && (
              <Chip
                color="anMainBlue"
                size="small"
                icon={<SchoolIcon />}
                sx={{ borderRadius: 1 }}
                label={parseInt(latestCourse.eLearning.percentage) + ' %'}
              />
            )}
            {pending && status.key !== 'pending' && (
              <Chip
                size="small"
                label="Pending Approval"
                color="warning"
                sx={{ borderRadius: 1 }}
              />
            )}
            {eLearning && (
              <Chip
                size="small"
                variant="outlined"
                icon={<SchoolIcon />}
                sx={{ borderRadius: 1 }}
                label="E-learning"
              />
            )}
            {enrolment && (
              <Chip
                size="small"
                color="info"
                variant="outlined"
                sx={{ borderRadius: 1 }}
                label={`In progress ${enrolment.percentComplete ?? 0}%`}
              />
            )}
          </Stack>
          {legacyOnly && (
            <Typography variant="body2">
              Your earlier e-learning result no longer counts. Please take the
              new course.
            </Typography>
          )}
        </Stack>
      </Paper>
    </Grid>
  )
}

export default CourseCard
